// OcApolloContainer.tsx
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRecoilState } from "recoil";
import _ from "lodash";
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
  from,
  split,
} from "@apollo/client";
import { ApolloProvider } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-sse";
import { CachePersistor, LocalStorageWrapper } from "apollo3-cache-persist";
import { OcLoading } from "oc/components";
import ApolloClientContext, {
  ApolloClientContextType,
} from "oc/context/apollo-client-context";
import { useOcCurrentUserState } from "oc/hooks";
import useOcApi from "oc/hooks/use-api";
import { ocRefreshTokenState } from "states";

/**
 * Create an Apollo Link for SSE subscriptions using graphql-sse
 * This replaces WebSocketLink for GraphQL Yoga compatibility
 */
class SSELink extends ApolloLink {
  private client: ReturnType<typeof createClient>;

  constructor(options: { url: string; getHeaders: () => Record<string, string> }) {
    super();
    this.client = createClient({
      url: options.url,
      headers: options.getHeaders,
      // Use single connection mode to avoid browser's 6 connection limit for SSE over HTTP/1.1
      // See: https://github.com/enisdenjo/graphql-sse/discussions/76
      singleConnection: true,
    });
  }

  public request(operation: any): Observable<any> {
    return new Observable((sink) => {
      return this.client.subscribe(
        {
          query: operation.query.loc?.source.body || "",
          variables: operation.variables,
          operationName: operation.operationName,
        },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        }
      );
    });
  }
}

interface OcApolloContainerProps {
  children: ReactNode;
}

export const OcApolloContainer: React.FC<OcApolloContainerProps> = ({
  children,
}) => {
  const { graphqlUrl: apiUrl } = useOcApi();
  const { contractCode, accessToken } = useOcCurrentUserState();

  const [client, setClient] = useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);
  const [persistor, setPersistor] =
    useState<CachePersistor<NormalizedCacheObject> | null>(null);

  const [refreshTokenError, setRefreshTokenError] =
    useRecoilState<boolean>(ocRefreshTokenState);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  // Use refs to always get the latest values in the link closures
  const contractCodeRef = useRef(contractCode);
  const accessTokenRef = useRef(accessToken);
  const isHiddenRef = useRef(isHidden);
  const refreshTokenErrorRef = useRef(refreshTokenError);

  // Update refs when values change
  useEffect(() => {
    contractCodeRef.current = contractCode;
  }, [contractCode]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    isHiddenRef.current = isHidden;
  }, [isHidden]);

  useEffect(() => {
    refreshTokenErrorRef.current = refreshTokenError;
  }, [refreshTokenError]);

  useLayoutEffect(() => {
    const handleHiddenState = () => setIsHidden(document.hidden);
    window.addEventListener("visibilitychange", handleHiddenState);
    return () =>
      window.removeEventListener("visibilitychange", handleHiddenState);
  }, []);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const cache = new InMemoryCache({
        typePolicies: {
          Subscription: {
            fields: {
              userRolesDataByContract: { merge: (_, incoming) => incoming },
              getEmailsByCompanyList: { merge: (_, incoming) => incoming },
              leaveRequestsByContract: { merge: (_, incoming) => incoming },
              permit: { merge: (_, incoming) => incoming },
            },
          },
          Mutation: {
            fields: {
              userRolesDataByContract: { merge: (_, incoming) => incoming },
            },
          },
          Query: {
            fields: {
              userRolesDataByContract: { merge: (_, incoming) => incoming },
              getCurrentUser: {
                merge: (existing, incoming) =>
                  Object.assign({}, existing, incoming),
              },
              getCafeteriaMenu: { merge: (_, incoming) => incoming },
              getCurrentCafeteriaStatement: {
                merge: (_, incoming) => incoming,
              },
              leaveYearlyDaysByContract: { merge: (_, incoming) => incoming },
              getContractFilterList: {
                keyArgs: false,
                merge: (existing, incoming) => {
                  if (existing) {
                    const newState = { ...incoming };
                    newState.availableList = _.uniqBy(
                      [...incoming.availableList],
                      "code",
                    );
                    newState.selectedList = _.uniqBy(
                      [...incoming.selectedList],
                      "code",
                    );
                    newState.searchedList = _.uniqBy(
                      [...incoming.searchedList],
                      "code",
                    );
                    return newState.availableList.length > newState.total
                      ? existing
                      : newState;
                  }
                  return incoming;
                },
              },
              orgUnitsAdminList: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              workingPlacesAdminList: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              jobClassGroupsAdminList: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              payOfficesAdminList: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              contractsAdminList: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              filteredCompanies: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              orgUnits: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              workingPlaces: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              jobClassGroups: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              payOffices: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              contracts: {
                keyArgs: false,
                merge: (existing, incoming) => ({ ...existing, ...incoming }),
              },
              shortContractList: {
                keyArgs: false,
                merge: (_, incoming) => incoming,
              },
            },
          },
        },
      });

      const httpLink = new HttpLink({ uri: apiUrl, credentials: "include" });

      // Create SSE link for subscriptions (GraphQL Yoga uses SSE instead of WebSocket)
      const sseLink = new SSELink({
        // graphql-sse uses the out-of-band protocol on /graphql/stream
        // (separate from the plain GraphQL endpoint). Backend wires
        // this up via useGraphQLSSE() in api.service.js serverOptions.
        url: `${apiUrl}/stream`,
        getHeaders: () => {
          const currentAccessToken = accessTokenRef.current;
          const currentContractCode = contractCodeRef.current;
          return {
            authorization: currentAccessToken ? `Bearer ${currentAccessToken}` : "",
            contractcode: currentContractCode ? `Contractcode ${currentContractCode}` : "",
          };
        },
      });

      console.log('🟢 [SSE] Subscription link initialized for:', `${apiUrl}/stream`);

      const splitLink = split(
        ({ query }) => {
          const def = getMainDefinition(query);
          return (
            def.kind === "OperationDefinition" &&
            def.operation === "subscription"
          );
        },
        sseLink,
        httpLink,
      );

      const authLink = setContext((_, { headers }) => {
        // Get current values from refs - always up to date!
        const currentAccessToken = accessTokenRef.current;
        const currentContractCode = contractCodeRef.current;

        return {
          headers: {
            ...headers,
            authorization: currentAccessToken ? `Bearer ${currentAccessToken}` : "",
            contractcode: currentContractCode ? `Contractcode ${currentContractCode}` : "",
          },
        };
      });

      const filterLink = new ApolloLink((operation, forward) => {
        const action = operation.operationName;
        const publicOperations = [
          "login",
          "owLogin",
          "ssoLogin",
          "serviceLogin",
          "forgotPassword",
          "property",
          "companies",
          "permit",
          "requestPermit",
          "addPermit",
        ];
        const isPublicOperation = publicOperations.includes(action);

        // 2FA operations should always be allowed (even when tab is hidden)
        const twoFactorOperations = [
          "verify2faTotpCode",
          "verify2faEmailCode",
          "sendEmail2faCode",
          "userByEmployee",
        ];
        const is2FAOperation = operation.operationName && twoFactorOperations.includes(operation.operationName);

        // Get current values from refs - always up to date!
        const currentContractCode = contractCodeRef.current;
        const currentAccessToken = accessTokenRef.current;
        const currentIsHidden = isHiddenRef.current;
        const currentRefreshTokenError = refreshTokenErrorRef.current;

        // If no contractCode, only allow public operations
        if (!currentContractCode) {
          if (isPublicOperation && !currentIsHidden) {
            return forward(operation);
          }
          // Block non-public operations when contractCode is missing
          return Observable.of();
        }

        // If contractCode exists, use normal filtering logic
        if (
          (isPublicOperation && !currentIsHidden) ||
          (action === "refreshToken" && currentAccessToken) ||
          (is2FAOperation && currentAccessToken) || // 2FA operations allowed even when hidden
          (!currentRefreshTokenError && currentAccessToken && !currentIsHidden)
        ) {
          return forward(operation);
        }
        // Block operation if none of the conditions are met
        return Observable.of();
      });

      const errorLink = onError(
        ({ graphQLErrors }) => {
          if (graphQLErrors) {
            graphQLErrors.forEach(
              ({ message }) => {
                if (
                  [
                    "Client needs to refresh the tokens!",
                    "All tokens are expired!",
                  ].includes(message) &&
                  !refreshTokenErrorRef.current
                ) {
                  setRefreshTokenError(true);
                }
              },
            );
          }
        },
      );

      const link = from([errorLink, authLink, filterLink, splitLink]);

      setClient(new ApolloClient({ link, cache }));
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiUrl,
  ]);

  // Separate effect for cache persistor - only runs when contractCode changes
  useEffect(() => {
    const initPersistor = async () => {
      if (contractCode && client) {
        const cache = client.cache as InMemoryCache;
        const newPersistor = new CachePersistor<NormalizedCacheObject>({
          cache,
          storage: new LocalStorageWrapper(window.sessionStorage),
          trigger: "write",
        });
        await newPersistor.restore();
        setPersistor(newPersistor);
      }
    };
    initPersistor();
  }, [contractCode, client]);

  const clearCache = useCallback(async (): Promise<void> => {
    if (!persistor || !client) return;

    // Stop all active queries before clearing the store
    // This prevents "Store reset while query was in flight" errors
    client.stop();

    await persistor.pause();
    await client.clearStore();
    await persistor.purge();
    window.sessionStorage.clear();
    setPersistor(null);
  }, [persistor, client]);

  if (!client) {
    return <OcLoading show />;
  }

  return (
    <ApolloProvider client={client}>
      <ApolloClientContext.Provider
        value={{ client, persistor, clearCache } as ApolloClientContextType}
      >
        {children}
      </ApolloClientContext.Provider>
    </ApolloProvider>
  );
};

export default OcApolloContainer;
